import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, lastValueFrom, map, Observable } from 'rxjs';

export enum PriceDisplayMode {
  Show = 'show',
  Hide = 'hide',
}

export enum CookiePreferences {
  Allow = 'allow',
  Disallow = 'disallow',
  NotSet = 'notset'
}

// The cloud only supports <string> as values (IPreferences must be a string-to-string dict)
export interface IPreferences {
  theme: string;
  language: string;
  locale: string;
  priceMode: PriceDisplayMode;
  allowCookies: CookiePreferences
}

@Injectable({
  providedIn: 'root'
})
export class PreferencesService {
  private static key = 'user-preferences';

  private loadedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private subject: BehaviorSubject<IPreferences> = new BehaviorSubject<IPreferences>(null!);
  private accessToken: string | null = null;
  private portalUserUrl: string | null = null;

  public get value(): IPreferences {
    return this.subject.value;
  }

  public get language(): Observable<string | null> {
    return this.subject.asObservable().pipe(map(p => p?.language ?? null));
  }

  public get locale(): Observable<string | null> {
    return this.subject.asObservable().pipe(map(p => p?.locale ?? null));
  }

  public get theme(): Observable<string | null> {
    return this.subject.asObservable().pipe(map(p => p?.theme ?? null));
  }

  public get priceMode(): Observable<PriceDisplayMode | null> {
    return this.subject.asObservable().pipe(map(p => p?.priceMode ?? null));
  }

  public get cookieConsent(): Observable<CookiePreferences | null> {
    return this.subject.asObservable().pipe(map(p => p?.allowCookies ?? null));
  }

  public get loaded(): Observable<boolean> {
    return this.loadedSubject.asObservable();
  }

  constructor(
    private http: HttpClient,
  ) {
  }

  public configureUrls(portalUserUrl: string): void {
    this.portalUserUrl = portalUserUrl;
  }

  public configureToken(accessToken: string): void {
    this.accessToken = accessToken;
  }

  public setDefaults(language: string, locale: string, theme: string): void {
    if (!!this.subject.value) {
      return;
    }

    this.subject.next({
      language,
      locale,
      theme,
      priceMode: PriceDisplayMode.Show,
      allowCookies: CookiePreferences.NotSet
    });
  }

  public async setLanguage(language: string): Promise<void> {
    if (this.subject.value?.language === language) {
      return;
    }

    return this.save({
      ...this.subject.value,
      language
    });
  }

  public async setLocale(locale: string): Promise<void> {
    if (this.subject.value.locale === locale) {
      return;
    }

    return this.save({
      ...this.subject.value,
      locale
    });
  }

  public async setTheme(theme: string): Promise<void> {
    if (this.subject.value.theme === theme) {
      return;
    }

    return this.save({
      ...this.subject.value,
      theme
    });
  }

  public async setPriceMode(priceMode: PriceDisplayMode): Promise<void> {
    if (this.subject.value.priceMode === priceMode) {
      return;
    }

    return this.save({
      ...this.subject.value,
      priceMode
    });
  }

  public async setCookieConsent(allowCookies: CookiePreferences): Promise<void> {
    if (this.subject.value.allowCookies === allowCookies) {
      return;
    }

    return this.save({
      ...this.subject.value,
      allowCookies
    });
  }

  public getCookieConsent(): CookiePreferences | undefined {
    if (this.subject.value.allowCookies == null 
      || this.subject.value.allowCookies == undefined 
      || this.subject.value.allowCookies == CookiePreferences.NotSet) {
      return CookiePreferences.NotSet;
    }

    return this.subject.value.allowCookies;
  }


  public getThemeFromLocalStorage(): string | undefined {
    let localPreferences: IPreferences | null = null;
    try {
      const data = JSON.parse(localStorage.getItem(PreferencesService.key)!);
      localPreferences = this.deserialize(data);
    }
    catch (e: any) {
      console.error(e);
    }

    if (localPreferences && localPreferences.theme) {
      return localPreferences.theme;
    }

    return undefined;
  }

  public async load(): Promise<void> {
    let result: IPreferences | null = null;

    // Try the app settings first
    if (this.appUrl() && this.accessToken) {
      try {
        // iOS doesn't allow to call the mobile app from an HTTPS
        // hosted page, so we have injected custom handlers to
        // deal with preferences.
        const getSetting = (window as any).webkit?.messageHandlers?.getSetting;
        if (getSetting) {
          const response = await getSetting.postMessage({key: PreferencesService.key});
          result = response ? this.deserialize(JSON.parse(response)) : null;
        } else {
          // http.get always deserializes the object if it is JSON, regardless of the content-type header. We can skip the JSON.parse step.
          const response = await lastValueFrom(this.http.get(`${this.appUrl()}/api/setting?key=${PreferencesService.key}`, this.createRequestOptions()));
          result = this.deserialize(response);
        }
      }
      catch (e: any) {
        console.error(e);
      }
    }

    // Try the portal settings
    if (!result && this.portalUserUrl && this.accessToken) {
      try {
        const response = await lastValueFrom(this.http.get(`${this.portalUserUrl}/api/v1/users/preferences`, this.createRequestOptions('application/json')));
        result = this.deserialize(response);
      }
      catch (e: any) {
        // If the backend returns a 404 then maybe this is a new user so just use default values
        if (e.status === 404) {
          result = { theme: this.subject.value.theme, language: this.subject.value.language, allowCookies: null, priceMode: this.subject.value.priceMode, locale: this.subject.value.locale }
        }
        console.error(e);
      }
    }

    // Ensure the local storage cache is updated with the most recent settings.
    // This should prevent wrong themes/languages being displayed during the time
    // between page load and getting the preferences from the app or server.
    if (result) {
      localStorage.setItem(PreferencesService.key, JSON.stringify(result));
    }

    // Try local storage
    if (!result) {
      try {
        const data = JSON.parse(localStorage.getItem(PreferencesService.key)!);
        result = this.deserialize(data);
      }
      catch (e: any) {
        console.error(e);
      }
    }

    if (result) {
      console.log('loaded user preferences', result);
      this.subject.next(result);
      this.loadedSubject.next(true);
    }
  }

  private async save(preferences: IPreferences): Promise<void> {
    if (!preferences) {
      return;
    }

    const jsonString = JSON.stringify(preferences);

    const savedToApp = this.saveToApp(jsonString);
    const savedToCloud = this.saveToCloud(preferences);

    localStorage.setItem(PreferencesService.key, jsonString);

    await savedToApp;
    await savedToCloud;

    this.subject.next({ ...preferences });
  }

  private async saveToApp(serialized: string): Promise<void> {
    if (this.appUrl() && this.accessToken) {
      try {
        // iOS doesn't allow to call the mobile app from an HTTPS
        // hosted page, so we have injected custom handlers to
        // deal with preferences.
        const putSetting = (window as any).webkit?.messageHandlers?.putSetting;
        if (putSetting) {
          await putSetting.postMessage({key: PreferencesService.key, value: serialized});
        } else {
          await lastValueFrom(this.http.put(`${this.appUrl()}/api/setting?key=${PreferencesService.key}`, serialized, this.createSaveRequestOptions()));
        }
      }
      catch (e: any) {
        console.error(e);
      }
    }
  }

  private async saveToCloud(serialized: IPreferences): Promise<void> {
    if (this.portalUserUrl && this.accessToken) {
      try {
        await lastValueFrom(this.http.put(`${this.portalUserUrl}/api/v1/users/preferences`, serialized, this.createSaveRequestOptions('application/json')));
      }
      catch (e: any) {
        console.error(e);
      }
    }
  }

  private appUrl(): string | undefined {
    return (<any>window).xpact as string;
  }

  private createSaveRequestOptions(contentType: string = 'text/plain'): { headers: { [key: string]: string; }; } {
    return ({
      headers: {
        'Authorization': `Bearer ${this.accessToken}`,
        'Content-Type': contentType,
      },
    });
  }

  private createRequestOptions(contentType: string = 'text/plain'): { headers: { [key: string]: string; }; } {
    return ({
      headers: {
        'Authorization': `Bearer ${this.accessToken}`,
        'Content-Type': contentType,
      },
    });
  }

  private deserialize(response: any): IPreferences | null {
    if (response && response !== '[object Object]') {
      return {
        ...this.subject.value,
        ...response,
      };
    }
    return null;
  }
}
